import { supabase } from './supabase';
import { cleanDisplayRemarks, cleanWorkTypeName } from './transactions';

/**
 * One description of each thing a site engineer can submit, shared by the
 * staff's "My Entries" screen and the admin's Approvals inbox.
 *
 * Both screens need the same human summary of a row. Describing an entry twice
 * is how the two views drift apart, so the definition lives here and adding
 * another entry type means adding one item to this list.
 *
 * Two of these tables are headers — a supplier bill and a usage batch — and
 * their line tables carry a `filter` so a line is never listed beside the
 * header that already stands for it.
 */

export const money = (paise: number) => `₹${Math.round(paise / 100).toLocaleString('en-IN')}`;
export const qty = (n: unknown) => Number(n).toLocaleString('en-IN', { maximumFractionDigits: 3 });
export const rel = (v: unknown, key = 'name') => (v as Record<string, string> | null)?.[key] ?? '—';

/** The material lines embedded under a purchase bill. */
const lines = (r: Row) => (r.material_purchases ?? []) as Array<Record<string, unknown>>;

/** The material lines embedded under a usage batch. */
const usageLines = (r: Row) => (r.material_usage ?? []) as Array<Record<string, unknown>>;

/** The material lines embedded under a stock transfer trip. */
const transferLines = (r: Row) => (r.stock_transfers ?? []) as Array<Record<string, unknown>>;

/** The trade lines embedded under a sub-contractor visit. */
const labourLines = (r: Row) => (r.subcontractor_labour ?? []) as Array<Record<string, unknown>>;

export type Row = Record<string, unknown>;

export interface EntrySource {
  table: string;
  kind: string;
  dateCol: string;
  /** Columns for the staff view — no submitter needed, it is always them. */
  select: string;
  /** Adds the submitter's name for the admin inbox. */
  selectWithSubmitter: string;
  describe: (r: Row) => string;
  /** Field-by-field breakdown shown when an admin expands an entry to review it. */
  detail: (r: Row) => Array<[string, string]>;
  /**
   * Extra narrowing for a table whose rows are not all independently
   * reviewable. material_purchases, material_usage and stock_transfers all
   * need it: a line belonging to a bill, a batch or a trip is reviewed as
   * part of that header, so listing it separately would ask the admin to
   * decide the same delivery — or the same slab pour, or the same lorry —
   * six times over.
   */
  filter?: (q: LooseQuery) => LooseQuery;
}

const submitter = ', submitted_by_profile:profiles!submitted_by(full_name)';

export const ENTRY_SOURCES: EntrySource[] = [
  {
    table: 'client_payments', kind: 'Payment', dateCol: 'paid_on',
    // No mode: client_payments does not record one — see 0015.
    select: 'id, paid_on, amount_paise, payment_type, reference_no, remarks, approval_status, submitted_at, submitted_by, reviewed_at, review_note, projects(name)',
    get selectWithSubmitter() { return this.select + submitter; },
    describe: (r) => `${money(Number(r.amount_paise))} · ${String(r.payment_type).replace('_', ' ')}`,
    detail: (r) => [
      ['Amount', money(Number(r.amount_paise))],
      ['Type', String(r.payment_type).replace('_', ' ')],
      ['Reference', (r.reference_no as string) || '—'],
      ['Remarks', (r.remarks as string) || '—'],
    ],
  },
  {
    table: 'purchase_bills', kind: 'Supplier bill', dateCol: 'bill_date',
    select: 'id, bill_date, bill_no, bill_total_paise, remarks, approval_status, submitted_at, submitted_by, reviewed_at, review_note, projects(name), suppliers(name), material_purchases(quantity, rate_paise, amount_paise, materials(name), units(short_name))',
    get selectWithSubmitter() { return this.select + submitter; },
    describe: (r) => {
      const n = lines(r).length;
      return `${n} material${n === 1 ? '' : 's'} · ${money(Number(r.bill_total_paise))} · ${rel(r.suppliers)}`;
    },
    // One row per material, so an admin can see the whole delivery without
    // opening anything else. The lines total is shown alongside the printed
    // total because a difference between them is the thing worth noticing.
    detail: (r) => {
      const ls = lines(r);
      const sum = ls.reduce((t, l) => t + Number(l.amount_paise), 0);
      const printed = Number(r.bill_total_paise);
      return [
        ['Supplier', rel(r.suppliers)],
        ['Bill no.', (r.bill_no as string) || '—'],
        ...ls.map((l): [string, string] => [
          rel(l.materials),
          `${qty(l.quantity)} ${rel(l.units, 'short_name')} × ${money(Number(l.rate_paise))} = ${money(Number(l.amount_paise))}`,
        ]),
        ['Lines total', money(sum)],
        ['Printed on the bill', money(printed)],
        ...(printed !== sum
          ? [['Difference', `${printed > sum ? '+' : '−'}${money(Math.abs(printed - sum))}`] as [string, string]]
          : []),
        ['Remarks', (r.remarks as string) || '—'],
      ];
    },
  },
  {
    table: 'material_purchases', kind: 'Material purchase', dateCol: 'purchased_on',
    select: 'id, purchased_on, quantity, rate_paise, amount_paise, advance_paid_paise, invoice_no, remarks, approval_status, submitted_at, submitted_by, reviewed_at, review_note, projects(name), materials(name), suppliers(name), units(short_name)',
    get selectWithSubmitter() { return this.select + submitter; },
    // Lines on a bill are reviewed with the bill, above.
    filter: (q) => q.is('bill_id', null),
    describe: (r) => `${qty(r.quantity)} ${rel(r.units, 'short_name')} · ${rel(r.materials)}`,
    detail: (r) => [
      ['Material', rel(r.materials)],
      ['Supplier', rel(r.suppliers)],
      ['Quantity', `${qty(r.quantity)} ${rel(r.units, 'short_name')}`],
      ['Rate', money(Number(r.rate_paise))],
      ['Total', money(Number(r.amount_paise))],
      ['Advance paid', money(Number(r.advance_paid_paise))],
      ['Balance', money(Number(r.amount_paise) - Number(r.advance_paid_paise))],
      ['Invoice no.', (r.invoice_no as string) || '—'],
    ],
  },
  {
    table: 'material_usage_batches', kind: 'Material used', dateCol: 'used_on',
    select: 'id, used_on, activity, remarks, approval_status, submitted_at, submitted_by, reviewed_at, review_note, projects(name), material_usage(quantity, materials(name), units(short_name))',
    get selectWithSubmitter() { return this.select + submitter; },
    describe: (r) => {
      const n = usageLines(r).length;
      return `${n} material${n === 1 ? '' : 's'}${r.activity ? ` · ${String(r.activity)}` : ''}`;
    },
    detail: (r) => [
      ['Work activity', (r.activity as string) || '—'],
      ...usageLines(r).map((l): [string, string] => [
        rel(l.materials),
        `${qty(l.quantity)} ${rel(l.units, 'short_name')}`,
      ]),
      ['Remarks', (r.remarks as string) || '—'],
    ],
  },
  {
    table: 'material_usage', kind: 'Material used', dateCol: 'used_on',
    select: 'id, used_on, quantity, activity, remarks, approval_status, submitted_at, submitted_by, reviewed_at, review_note, projects(name), materials(name), units(short_name)',
    get selectWithSubmitter() { return this.select + submitter; },
    // A line inside a batch is reviewed with the batch, above.
    filter: (q) => q.is('batch_id', null),
    describe: (r) => `${qty(r.quantity)} ${rel(r.units, 'short_name')} · ${rel(r.materials)}`,
    detail: (r) => [
      ['Material', rel(r.materials)],
      ['Quantity used', `${qty(r.quantity)} ${rel(r.units, 'short_name')}`],
      ['Work activity', (r.activity as string) || '—'],
      ['Remarks', (r.remarks as string) || '—'],
    ],
  },
  {
    table: 'subcontractor_labour_batches', kind: 'Attendance', dateCol: 'worked_on',
    select: 'id, worked_on, remarks, approval_status, submitted_at, submitted_by, reviewed_at, review_note, projects(name), subcontractors(name), subcontractor_labour(worker_count, rate_per_worker_paise, total_amount_paise, work_types(name))',
    get selectWithSubmitter() { return this.select + submitter; },
    describe: (r) => {
      const n = labourLines(r).length;
      return `${n} work type${n === 1 ? '' : 's'} · ${rel(r.subcontractors)}`;
    },
    detail: (r) => [
      ['Sub-contractor', rel(r.subcontractors)],
      ...labourLines(r).map((l): [string, string] => [
        cleanWorkTypeName(rel(l.work_types)),
        `${l.worker_count} workers${l.rate_per_worker_paise ? ` × ${money(Number(l.rate_per_worker_paise))}` : ''} = ${money(Number(l.total_amount_paise))}`,
      ]),
      ['Remarks', cleanDisplayRemarks(r.remarks as string) || '—'],
    ],
  },
  {
    table: 'subcontractor_labour', kind: 'Attendance', dateCol: 'worked_on',
    select: 'id, worked_on, worker_count, rate_per_worker_paise, total_amount_paise, remarks, approval_status, submitted_at, submitted_by, reviewed_at, review_note, projects(name), subcontractors(name), work_types(name)',
    get selectWithSubmitter() { return this.select + submitter; },
    // A trade that belongs to a visit is reviewed with the visit, above.
    filter: (q) => q.is('batch_id', null),
    describe: (r) => `${r.worker_count} × ${cleanWorkTypeName(rel(r.work_types))} · ${rel(r.subcontractors)}`,
    detail: (r) => [
      ['Sub-contractor', rel(r.subcontractors)],
      ['Work type', cleanWorkTypeName(rel(r.work_types))],
      ['Number of workers', String(r.worker_count)],
      ['Rate per worker', r.rate_per_worker_paise ? money(Number(r.rate_per_worker_paise)) : '—'],
      ['Total amount', money(Number(r.total_amount_paise))],
      ['Remarks', cleanDisplayRemarks(r.remarks as string) || '—'],
    ],
  },
  {
    table: 'expenses', kind: 'Site expense', dateCol: 'spent_on',
    select: 'id, spent_on, title, amount_paise, paid_to, bill_no, remarks, approval_status, submitted_at, submitted_by, reviewed_at, review_note, projects(name), expense_categories(name)',
    get selectWithSubmitter() { return this.select + submitter; },
    describe: (r) => `${money(Number(r.amount_paise))} · ${String(r.title)}`,
    detail: (r) => [
      ['What', String(r.title)],
      ['Category', rel(r.expense_categories)],
      ['Amount', money(Number(r.amount_paise))],
      ['Paid to', (r.paid_to as string) || '—'],
      ['Bill no.', (r.bill_no as string) || '—'],
      ['Remarks', (r.remarks as string) || '—'],
    ],
  },
  {
    table: 'stock_transfer_batches', kind: 'Stock transfer', dateCol: 'transferred_on',
    select: 'id, transferred_on, vehicle_no, driver_name, remarks, approval_status, submitted_at, submitted_by, reviewed_at, review_note, projects!stock_transfer_batches_from_project_id_fkey(name), to_project:projects!stock_transfer_batches_to_project_id_fkey(name), stock_transfers(quantity, materials(name), units(short_name))',
    get selectWithSubmitter() { return this.select + submitter; },
    describe: (r) => {
      const n = transferLines(r).length;
      return `${n} material${n === 1 ? '' : 's'} → ${rel(r.to_project)}`;
    },
    detail: (r) => [
      ['From site', rel(r.projects)],
      ['To site', rel(r.to_project)],
      ...transferLines(r).map((l): [string, string] => [
        rel(l.materials),
        `${qty(l.quantity)} ${rel(l.units, 'short_name')}`,
      ]),
      ['Vehicle', (r.vehicle_no as string) || '—'],
      ['Driver', (r.driver_name as string) || '—'],
      ['Remarks', (r.remarks as string) || '—'],
    ],
  },
  {
    table: 'stock_transfers', kind: 'Stock transfer', dateCol: 'transferred_on',
    select: 'id, transferred_on, quantity, vehicle_no, driver_name, remarks, approval_status, submitted_at, submitted_by, reviewed_at, review_note, materials(name), units(short_name), projects!stock_transfers_from_project_id_fkey(name), to_project:projects!stock_transfers_to_project_id_fkey(name)',
    get selectWithSubmitter() { return this.select + submitter; },
    // A line that belongs to a trip is reviewed with the trip, above.
    filter: (q) => q.is('batch_id', null),
    describe: (r) => `${qty(r.quantity)} ${rel(r.units, 'short_name')} ${rel(r.materials)} → ${rel(r.to_project)}`,
    detail: (r) => [
      ['Material', rel(r.materials)],
      ['Quantity', `${qty(r.quantity)} ${rel(r.units, 'short_name')}`],
      ['To site', rel(r.to_project)],
      ['Vehicle', (r.vehicle_no as string) || '—'],
      ['Driver', (r.driver_name as string) || '—'],
      ['Remarks', (r.remarks as string) || '—'],
    ],
  },
];

/**
 * The six tables have different row shapes, and typing a query whose table name
 * is a union of six literals produces a union too large for TypeScript to
 * represent (TS2590). This loose handle keeps the dynamic query buildable;
 * every field is narrowed by hand where it is read.
 */
export type LooseQuery = {
  select(cols: string): LooseQuery;
  eq(col: string, value: unknown): LooseQuery;
  is(col: string, value: unknown): LooseQuery;
  in(col: string, values: unknown[]): LooseQuery;
  order(col: string, opts: { ascending: boolean }): LooseQuery;
  limit(n: number): LooseQuery;
  update(values: Record<string, unknown>): LooseQuery;
  then: Promise<{ data: unknown[] | null; error: { message: string } | null }>['then'];
};

export const looseFrom = supabase.from.bind(supabase) as unknown as (table: string) => LooseQuery;
